import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { Tag } from '../../queries/tags';
import { Track, useUpdateTrackTags } from '../../queries/tracks';

interface EditTrackTagsDialogProps {
  track: Track;
  allTags: Tag[];
  onClose: () => void;
}

export function EditTrackTagsDialog({ track, allTags, onClose }: EditTrackTagsDialogProps) {
  const [selectedTagIds, setSelectedTagIds] = useState<Set<number>>(new Set(track.tags.map((t) => t.id)));
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateTrackTags();

  const toggleTag = (tagId: number) =>
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });

  const handleSave = () => {
    setError(null);
    update.mutate(
      { trackId: track.track_id, tagIds: Array.from(selectedTagIds) },
      { onSuccess: onClose, onError: (err) => setError(extractErrorMessage(err, 'Failed to update tags.')) },
    );
  };

  const tagsByCategory = allTags.reduce<Record<string, Tag[]>>((acc, tag) => {
    (acc[tag.category] ??= []).push(tag);
    return acc;
  }, {});

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">Edit tags — {track.name}</Dialog.Title>

          <div className="mt-4 max-h-96 space-y-4 overflow-y-auto">
            {Object.entries(tagsByCategory).map(([category, tags]) => (
              <div key={category}>
                <h3 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">{category}</h3>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <label
                      key={tag.id}
                      className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
                        selectedTagIds.has(tag.id)
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-input text-muted-foreground hover:border-foreground/30'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTagIds.has(tag.id)}
                        onChange={() => toggleTag(tag.id)}
                        className="sr-only"
                      />
                      {tag.name}
                    </label>
                  ))}
                </div>
              </div>
            ))}
            {allTags.length === 0 && <p className="text-sm text-muted-foreground">No tags defined yet.</p>}
          </div>
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={update.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
